import { useImportLog, type ImportEntityType } from "@/hooks/use-monday-import";
import { useUsers, buildUserMap } from "@/hooks/use-users";
import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";

interface Props {
  userId?: string | null;
  entityType?: ImportEntityType;
}

/** Tabell over tidligere import-kjøringer (fra import_log). */
export function ImportLog({ userId, entityType }: Props) {
  const { data: log = [], isLoading } = useImportLog(userId, entityType);
  const { data: users = [] } = useUsers();
  const userMap = buildUserMap(users);

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Henter import-logg...</p>;
  }
  if (log.length === 0) {
    return <p className="text-sm text-muted-foreground">Ingen import-kjøringer ennå.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-[11px] uppercase tracking-wider text-muted-foreground">
            <th className="text-left py-2 px-2 font-medium">Når</th>
            <th className="text-left py-2 px-2 font-medium">Ansatt</th>
            <th className="text-left py-2 px-2 font-medium">Modus</th>
            <th className="text-right py-2 px-2 font-medium">Importert</th>
            <th className="text-right py-2 px-2 font-medium">Hoppet over</th>
            <th className="text-left py-2 px-2 font-medium">Match-fordeling</th>
            <th className="text-right py-2 px-2 font-medium">Grupper</th>
          </tr>
        </thead>
        <tbody>
          {log.map((row) => {
            const user = row.user_id ? userMap.get(row.user_id) : null;
            const dist = row.match_distribution ?? {};
            return (
              <tr key={row.id} className="border-b border-border/40">
                <td className="py-2 px-2 tabular-nums text-muted-foreground">
                  {formatDate(row.created_at)}
                </td>
                <td className="py-2 px-2">{user?.full_name ?? "—"}</td>
                <td className="py-2 px-2">
                  <span
                    className={cn(
                      "px-1.5 py-0.5 rounded text-[10px] font-medium",
                      row.dry_run
                        ? "bg-blue-100 text-blue-700"
                        : "bg-green-100 text-green-700",
                    )}
                  >
                    {row.dry_run ? "Dry-run" : "Import"}
                  </span>
                </td>
                <td className="py-2 px-2 text-right tabular-nums font-medium">
                  {row.items_imported}
                </td>
                <td className="py-2 px-2 text-right tabular-nums text-muted-foreground">
                  {row.items_skipped}
                </td>
                <td className="py-2 px-2 text-xs text-muted-foreground">
                  {Object.entries(dist)
                    .filter(([, v]) => Number(v) > 0)
                    .map(([k, v]) => `${k}: ${v}`)
                    .join(", ") || "—"}
                </td>
                <td className="py-2 px-2 text-right tabular-nums text-muted-foreground">
                  {row.group_ids?.length ?? 0}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
